import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

export type BadgeVariant = 'normal' | 'glass' | 'overlay';
export type BadgeSize = 'sm' | 'md' | 'lg';
export type BadgeState =
  | 'neutral'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger';

@Component({
  selector: 'om-badge',
  templateUrl: './badge.html',
  styleUrl: './badge.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Badge {
  label = input.required<string>();
  variant = input<BadgeVariant>('normal');
  size = input<BadgeSize>('md');
  state = input<BadgeState>('primary');
  showDot = input(true);
  pulseDot = input(false);

  readonly isGlass = computed(() => this.variant() === 'glass' || this.variant() === 'overlay');

  readonly containerClasses = computed(() => {
    const sizeMap: Record<BadgeSize, string> = {
      sm: 'px-3 py-1.5 text-[11px]',
      md: 'px-4 py-2 text-xs',
      lg: 'px-5 py-2.5 text-sm',
    };

    const normalStateMap: Record<BadgeState, string> = {
      neutral:
        'bg-stone-100 text-stone-700 border border-stone-200 dark:bg-stone-800 dark:text-stone-200 dark:border-stone-700',
      primary:
        'bg-primary/10 text-primary border border-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/30',
      success:
        'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/25 dark:text-emerald-300 dark:border-emerald-700/60',
      warning:
        'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/25 dark:text-amber-300 dark:border-amber-700/60',
      danger:
        'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/25 dark:text-red-300 dark:border-red-700/60',
    };

    const glassStateMap: Record<BadgeState, string> = {
      neutral:
        'relative isolate overflow-hidden bg-white/[0.16] dark:bg-black/[0.22] text-white border border-white/35 dark:border-white/20 backdrop-blur-xl',
      primary:
        'relative isolate overflow-hidden bg-primary/25 dark:bg-primary/30 text-white border border-primary/40 dark:border-primary/30 backdrop-blur-xl',
      success:
        'relative isolate overflow-hidden bg-emerald-500/18 text-white border border-emerald-200/45 dark:border-emerald-200/30 backdrop-blur-xl',
      warning:
        'relative isolate overflow-hidden bg-amber-500/18 text-white border border-amber-200/45 dark:border-amber-200/30 backdrop-blur-xl',
      danger:
        'relative isolate overflow-hidden bg-red-500/18 text-white border border-red-200/45 dark:border-red-200/30 backdrop-blur-xl',
    };

    const overlayStateMap: Record<BadgeState, string> = {
      neutral:
        'bg-white/70 dark:bg-black/50 backdrop-blur-md text-slate-800 dark:text-white border border-white/50 dark:border-white/15 shadow-[0_2px_8px_rgba(0,0,0,0.15)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.5)]',
      primary:
        'bg-primary/70 dark:bg-primary/60 backdrop-blur-md text-white border border-primary/50 dark:border-primary/30 shadow-[0_2px_8px_rgba(0,0,0,0.15)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.5)]',
      success:
        'bg-emerald-500/70 dark:bg-emerald-500/60 backdrop-blur-md text-white border border-emerald-400/50 dark:border-emerald-400/30 shadow-[0_2px_8px_rgba(0,0,0,0.15)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.5)]',
      warning:
        'bg-amber-500/70 dark:bg-amber-500/60 backdrop-blur-md text-white border border-amber-400/50 dark:border-amber-400/30 shadow-[0_2px_8px_rgba(0,0,0,0.15)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.5)]',
      danger:
        'bg-red-500/70 dark:bg-red-500/60 backdrop-blur-md text-white border border-red-400/50 dark:border-red-400/30 shadow-[0_2px_8px_rgba(0,0,0,0.15)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.5)]',
    };

    const variantMap: Record<BadgeVariant, Record<BadgeState, string>> = {
      normal: normalStateMap,
      glass: glassStateMap,
      overlay: overlayStateMap,
    };
    const palette = variantMap[this.variant()];

    return `inline-flex items-center gap-2 rounded-full font-medium tracking-wide ${sizeMap[this.size()]} ${palette[this.state()]}`;
  });

  readonly dotClasses = computed(() => {
    const dotStateMap: Record<BadgeState, string> = {
      neutral: 'bg-stone-500',
      primary: 'bg-primary',
      success: 'bg-emerald-500',
      warning: 'bg-amber-500',
      danger: 'bg-red-500',
    };
    const color =
      this.variant() === 'glass' && this.state() === 'neutral'
        ? 'bg-primary'
        : dotStateMap[this.state()];
    const pulse = this.pulseDot() ? 'animate-pulse' : '';
    return `w-2 h-2 rounded-full shrink-0 ${color} ${pulse}`;
  });

  readonly labelClasses = computed(() => {
    switch (this.variant()) {
      case 'glass':
        return 'text-white drop-shadow-md shadow-black';
      case 'overlay':
        return 'text-current font-bold uppercase tracking-wider';
      default:
        return 'text-current';
    }
  });
}

